import { ThemeEvents, CartAddEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';
import { addItemsAndGoCart } from './belabelly-cart-go-checkout.js';

/**
 * @param {HTMLFormElement | null | undefined} form
 * @returns {Record<string, string>}
 */
function collectLineItemProperties(form) {
  const props = {};
  if (!form) return props;

  /** @param {string} key @param {FormDataEntryValue} value */
  function addProp(key, value) {
    const match = key.match(/^properties\[(.+)\]$/);
    if (!match) return;
    const trimmed = String(value || '').trim();
    if (trimmed) props[match[1]] = trimmed;
  }

  new FormData(form).forEach((value, key) => addProp(key, value));

  if (form.id) {
    document.querySelectorAll('[form="' + form.id + '"]').forEach(function (field) {
      if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement || field instanceof HTMLTextAreaElement)) {
        return;
      }
      if (field.name) addProp(field.name, field.value);
    });
  }

  return props;
}

window.BelabellyPdpLineItem = window.BelabellyPdpLineItem || {};
window.BelabellyPdpLineItem.collectFromForm = collectLineItemProperties;

function goToCartPage() {
  const url = window.Theme?.routes?.cart_url || '/cart';
  window.location.assign(url);
  return true;
}

function openCartDrawer() {
  const drawer = document.querySelector('cart-drawer-component');
  if (!drawer) return false;

  if (typeof drawer.open === 'function') {
    drawer.open();
    return true;
  }

  if (typeof drawer.showDialog === 'function') {
    drawer.showDialog();
    return true;
  }

  return false;
}

window.BelabellyCartDrawer = window.BelabellyCartDrawer || {};
window.BelabellyCartDrawer.open = goToCartPage;
window.BelabellyCartDrawer.goToCart = goToCartPage;

/**
 * @param {Array<{ id: number; quantity: number; properties?: Record<string, string> }>} items
 * @param {{ errorMessage?: string; source?: string; sourceId?: string; productId?: string; itemCount?: number }} [options]
 */
async function addFollowerItemsToCart(items, options = {}) {
  if (!items?.length) {
    throw new Error(options.errorMessage || 'Nenhum item para adicionar.');
  }

  const sectionIds = [];
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) {
      sectionIds.push(el.dataset.sectionId);
    }
  });

  const body = { items };
  if (sectionIds.length) body.sections = sectionIds.join(',');

  const res = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });

  let data = {};
  try {
    data = await res.json();
  } catch {
    data = {};
  }

  const failed =
    !res.ok ||
    (data && typeof data === 'object' && (data.status === 'error' || Number(data.status) >= 400 || data.description));

  if (failed) {
    const record = /** @type {Record<string, string>} */ (data);
    throw new Error(
      record.description ||
        record.message ||
        options.errorMessage ||
        'Não foi possível adicionar ao carrinho. Tente de novo.'
    );
  }

  let cart = data;
  try {
    const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (cartRes.ok) cart = await cartRes.json();
  } catch {
    /* ok */
  }

  const itemCount =
    options.itemCount ?? items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);

  document.dispatchEvent(
    new CartAddEvent(cart, options.sourceId || 'belabelly-pdp-follower-multi', {
      source: options.source || 'belabelly-pdp-follower-multi',
      itemCount,
      productId: options.productId,
      sections: data.sections,
    })
  );

  goToCartPage();

  return cart;
}

/**
 * @param {HTMLElement | null | undefined} root
 */
function clearKitValidation(root) {
  if (!root) return;
  const banner = root.querySelector('[data-md-kit-validation]');
  if (banner) banner.hidden = true;
  root.querySelectorAll('.md-pdp-field--error').forEach(function (el) {
    el.classList.remove('md-pdp-field--error');
  });
}

/**
 * @param {HTMLElement | null | undefined} root
 * @param {string} message
 */
function showKitValidation(root, message) {
  if (!root) return;
  const banner = root.querySelector('[data-md-kit-validation]');
  if (!banner) return;
  banner.textContent = message;
  banner.hidden = false;
  banner.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
}

/**
 * @param {HTMLFormElement | null | undefined} form
 * @param {HTMLElement | null | undefined} [root]
 * @returns {boolean}
 */
function validateFormAssociatedFields(form, root) {
  root = root || (form && form.closest('[data-md-pdp]'));
  if (!form || !root) return true;
  if (root.dataset.pdpWholesale === 'true') return true;

  clearKitValidation(root);

  const message =
    root.dataset.kitValidationMessage ||
    'Selecione os tamanhos e preencha as gravações antes de comprar.';
  const formId = form.id;
  const seen = new Set();
  /** @type {HTMLElement[]} */
  const invalid = [];

  /**
   * @param {Element | null | undefined} field
   */
  function noteInvalid(field) {
    if (!field || !(field instanceof HTMLElement) || seen.has(field)) return;
    seen.add(field);
    const value = String(field.value || '').trim();
    if (!value) invalid.push(field);
  }

  if (formId) {
    document.querySelectorAll('[form="' + formId + '"]').forEach(function (field) {
      if (!(field instanceof HTMLElement)) return;
      if (field.matches('select[required], input[required], textarea[required]')) {
        noteInvalid(field);
        return;
      }
      if (field.matches('.md-pdp-gravacao__input') || field.closest('[data-md-gravacao-fields]')) {
        noteInvalid(field);
      }
    });
  }

  root.querySelectorAll('[data-md-kit-sizes] select, [data-md-kit-extra-size] select').forEach(noteInvalid);
  root.querySelectorAll('[data-md-gravacao-fields] input[type="text"]').forEach(noteInvalid);

  if (!invalid.length) return true;

  invalid.forEach(function (field) {
    const wrap = field.closest(
      '.md-pdp-kit-sizes__field, .md-pdp-gravacao__field, .variant-option, .md-pdp-kit-sizes'
    );
    if (wrap) wrap.classList.add('md-pdp-field--error');
  });

  invalid[0].focus();
  if (typeof invalid[0].reportValidity === 'function') invalid[0].reportValidity();
  showKitValidation(root, message);
  return false;
}

window.BelabellyPdpLineItem.validateFormAssociatedFields = validateFormAssociatedFields;
window.BelabellyPdpLineItem.clearKitValidation = clearKitValidation;

/**
 * Belabelly PDP — galeria, preços, variantes (ES5-friendly, sem class/#).
 * @param {HTMLElement} root
 */
function BelabellyProductPage(root) {
  this.root = root;
  this.moneyFormat = root.dataset.moneyFormat || '{{amount}}';
  this.currencyCode = root.dataset.currencyCode || 'BRL';
  this.pixDiscount = Number(root.dataset.pixDiscount || 5) / 100;
  this.installments = Number(root.dataset.installments || 12);
  this.cashbackPercent = Number(root.dataset.cashbackPercent || 0) / 100;
  this.freeShippingMin = Number(root.dataset.freeShippingMin || 29900);
  this.viewersMin = Number(root.dataset.viewersMin || 18);
  this.viewersMax = Number(root.dataset.viewersMax || 98);
  this.tplFreeShipping = root.dataset.templateFreeShipping || 'Frete grátis';
  this.tplFreeShippingFrom =
    root.dataset.templateFreeShippingFrom || 'Frete grátis a partir de __AMOUNT__';
  this.promoEnd = root.dataset.promoEnd || '';
  this.tplViewers = root.dataset.templateViewers || '__COUNT__ vendo agora';
  this.galleryMap = this.parseGalleryMap();

  this.root._belabellyPdp = this;

  this.bindGalleryZoom();
  this.bindGalleryVideoExpand();
  this.syncGalleryVideoAutoplay();
  this.bindVariantPickerGallery();
  this.bindBuyNow();
  this.bindAddCartBusy();
  this.bindNavScroll();
  this.bindVariantUpdates();
  this.bindFormVariantSync();
  this.bindKitValidationClear();
  this.followerMulti = null;
  if (this.root.dataset.pdpFollowerRetail === 'true') {
    this.initFollowerMultiModule();
    this.initFollowerStickyModule();
  }
  this.bindFollowerVariantSync();
  this.initDeliveryEstimate();

  const initialPrice = Number(root.dataset.variantPrice || 0);
  const initialCompare = Number(root.dataset.variantCompare || 0);
  if (initialPrice > 0 && this.root.dataset.pdpFollowerRetail !== 'true') {
    this.updatePrices({
      price: initialPrice,
      compare_at_price: initialCompare > initialPrice ? initialCompare : null,
    });
  } else if (this.root.dataset.pdpFollowerRetail === 'true') {
    this.syncFollowerPriceFromStamp();
  } else {
    this.updateShippingTag(initialPrice);
  }

}

BelabellyProductPage.prototype.syncFollowerPriceFromStamp = function () {
  const checked = this.root.querySelector(
    '.belabelly-pdp-variant-option--estampa input[type="radio"]:checked, .belabelly-pdp-variant-option--estampa input[type="radio"]'
  );
  if (checked instanceof HTMLInputElement && checked.dataset.variantPrice) {
    const price = Number(checked.dataset.variantPrice);
    if (Number.isFinite(price) && price > 0) {
      this.updatePrices({ price, compare_at_price: null });
      return;
    }
  }

  const initialPrice = Number(this.root.dataset.variantPrice || 0);
  if (initialPrice > 0) {
    this.updatePrices({ price: initialPrice, compare_at_price: null });
  }
};

BelabellyProductPage.prototype.initFollowerMultiModule = function () {
  const self = this;
  const assetV = self.root.dataset.belabellyAssetV || String(Date.now());
  const url = new URL(`./belabelly-pdp-follower-multi.js?v=${assetV}`, import.meta.url).href;

  import(url)
    .then((mod) => {
      if (typeof mod.initFollowerMulti !== 'function') return;
      self.followerMulti = mod.initFollowerMulti(self);
      self.syncFollowerPriceFromStamp();
    })
    .catch((error) => {
      console.warn('[Belabelly PDP] follower multi indisponível', error);
      self.syncFollowerPriceFromStamp();
    });
};

BelabellyProductPage.prototype.initFollowerStickyModule = function () {
  const self = this;
  const assetV = self.root.dataset.belabellyAssetV || String(Date.now());
  const url = new URL(`./belabelly-pdp-follower-sticky.js?v=${assetV}`, import.meta.url).href;

  import(url)
    .then((mod) => {
      if (typeof mod.initFollowerSticky !== 'function') return;
      mod.initFollowerSticky(self.root);
    })
    .catch((error) => {
      console.warn('[Belabelly PDP] follower sticky indisponível', error);
    });
};

BelabellyProductPage.prototype.parseGalleryMap = function () {
  const el = this.root.querySelector('[data-md-gallery-variant-map]');
  if (!el || !el.textContent) return null;
  try {
    return JSON.parse(el.textContent);
  } catch (e) {
    console.warn('[Belabelly PDP] mapa da galeria inválido', e);
    return null;
  }
};

BelabellyProductPage.prototype.getSelectedVariantId = function () {
  const form = this.root.querySelector('form[action*="/cart/add"]');
  if (!form) return null;
  const input = form.querySelector('[name="id"]');
  return input && input.value ? String(input.value) : null;
};

BelabellyProductPage.prototype.getMaxGallerySlots = function () {
  const grid = this.root.querySelector('[data-md-gallery-grid]');
  const map = this.galleryMap;
  if (window.BelabellyPdpGallery && window.BelabellyPdpGallery.galleryMaxSlots) {
    return window.BelabellyPdpGallery.galleryMaxSlots(
      map || { maxSlots: Number(grid && grid.dataset.slots) }
    );
  }
  const n = (map && map.maxSlots) || Number((grid && grid.dataset.slots) || 2) || 2;
  return n > 2 ? 2 : n;
};

BelabellyProductPage.prototype.getGalleryBackSlot = function () {
  const index =
    window.BelabellyPdpGallery && window.BelabellyPdpGallery.galleryBackSlotIndex
      ? window.BelabellyPdpGallery.galleryBackSlotIndex(this.galleryMap)
      : 1;
  return { index: index, attr: String(index + 1) };
};

BelabellyProductPage.prototype.resolveWholesaleVisualGroup = function () {
  const group = this.root.querySelector('[data-vf-color-group]');
  if (!group) return null;
  const selected = group.querySelector('[data-vf-option-value].is-selected');
  if (!selected) return null;
  const value = selected.dataset.vfOptionValue;
  return value != null ? String(value).trim() : null;
};

BelabellyProductPage.prototype.resolveVisualGroupValue = function () {
  const map = this.galleryMap;
  if (!map) return null;

  const wholesaleGroup = this.resolveWholesaleVisualGroup();
  if (wholesaleGroup) return wholesaleGroup;

  const picker = this.root.querySelector('variant-picker.belabelly-pdp-variant-picker');
  if (picker) {
    const visualFieldset = picker.querySelector('[data-md-visual-option="true"]');
    if (visualFieldset) {
      const visualChecked = visualFieldset.querySelector('input[type="radio"]:checked');
      if (visualChecked) return visualChecked.value?.trim() || null;
    }
  }

  const visualKey = map.visualOption || 'option1';
  const pos = visualKey === 'option2' ? 2 : visualKey === 'option3' ? 3 : 1;
  if (!picker) return null;

  for (const fieldset of picker.querySelectorAll('fieldset')) {
    const checked = fieldset.querySelector('input[type="radio"]:checked');
    if (!checked) continue;
    const inputPos = Number(checked.dataset.inputId?.split('-')[0] || 0);
    if (inputPos === pos) {
      const value = checked.value?.trim();
      return value || null;
    }
  }

  if (map.resolver?.groupsOrdered?.length === 1) {
    return map.resolver.groupsOrdered[0];
  }
  return null;
};

BelabellyProductPage.prototype.getVariantImageIds = function (variant) {
  const map = this.galleryMap;
  if (!map) return [];

  let groupValue = this.resolveVisualGroupValue();

  if (!groupValue && variant && window.BelabellyPdpGallery) {
    groupValue = window.BelabellyPdpGallery.groupForVariant(
      variant,
      map.visualOption || 'option1'
    );
  }

  const visualKey = map.visualOption || 'option1';
  if (!groupValue && variant) {
    const entry =
      variant.id != null && map.variants ? map.variants[String(variant.id)] : null;
    groupValue =
      entry?.groupValue != null
        ? entry.groupValue
        : variant[visualKey] != null
          ? variant[visualKey]
          : null;
  }

  if (
    groupValue &&
    map.byVisual &&
    map.byVisual[groupValue] &&
    map.byVisual[groupValue].imageIds &&
    map.byVisual[groupValue].imageIds.length
  ) {
    return this.orderGalleryImageIds(
      map.byVisual[groupValue].imageIds.map(String),
      groupValue,
      variant
    );
  }

  if (window.BelabellyPdpGallery && map.resolver && groupValue) {
    const computed = window.BelabellyPdpGallery.computeForGroup(map, groupValue);
    if (computed?.length) return this.orderGalleryImageIds(computed, groupValue, variant);
  }

  const variantId =
    variant && variant.id != null
      ? String(variant.id)
      : this.getSelectedVariantId();

  if (!variantId || !map.variants || !map.variants[variantId]) return [];

  const entry = map.variants[variantId];
  const imageIds = entry.imageIds || entry.mediaIds || [];
  if (!imageIds || !imageIds.length) return [];
  return this.orderGalleryImageIds(imageIds.map(String), groupValue, variant);
};

BelabellyProductPage.prototype.orderGalleryImageIds = function (imageIds, groupValue, variant) {
  const map = this.galleryMap;
  if (
    !imageIds?.length ||
    !groupValue ||
    !window.BelabellyPdpGallery?.orderGroupImageIds ||
    !map?.resolver?.images
  ) {
    return imageIds;
  }

  const include = {};
  imageIds.forEach((id) => {
    include[String(id)] = true;
  });

  return (
    window.BelabellyPdpGallery.orderGroupImageIds(
      map.resolver.images,
      include,
      groupValue,
      map.allVariants || [],
      map.visualOption || 'option1',
      this.getFeaturedImageId(variant)
    ) || imageIds
  );
};

BelabellyProductPage.prototype.buildGalleryGridIds = function (imageIds) {
  if (!imageIds || !imageIds.length) return [];
  if (window.BelabellyPdpGallery?.buildGalleryGridIds) {
    return window.BelabellyPdpGallery.buildGalleryGridIds(imageIds, this.galleryMap);
  }
  return imageIds.slice(0, this.getMaxGallerySlots());
};

BelabellyProductPage.prototype.buildGalleryFullIds = function (imageIds) {
  if (!imageIds || !imageIds.length) return [];
  if (window.BelabellyPdpGallery?.buildGalleryFullIds) {
    return window.BelabellyPdpGallery.buildGalleryFullIds(imageIds, this.galleryMap);
  }
  return imageIds;
};

BelabellyProductPage.prototype.getGridImageIdsFromList = function (imageIds) {
  return this.buildGalleryGridIds(imageIds);
};

BelabellyProductPage.prototype.getGalleryImageIds = function (variant) {
  const v =
    variant ||
    this.getPickerVariant() ||
    (this.getSelectedVariantId() ? { id: this.getSelectedVariantId() } : null);
  return this.getVariantImageIds(v);
};

BelabellyProductPage.prototype.getFeaturedImageId = function (variant) {
  if (!variant) return null;
  if (variant.featured_media && variant.featured_media.id != null) {
    return String(variant.featured_media.id);
  }
  if (variant.featured_image && variant.featured_image.id != null) {
    return String(variant.featured_image.id);
  }
  return null;
};

BelabellyProductPage.prototype.ensureGalleryLightbox = function () {
  if (this._galleryLightbox) return this._galleryLightbox;

  const dialog = this.root.querySelector('[data-md-gallery-lightbox]');
  if (!(dialog instanceof HTMLDialogElement)) return null;

  const track = dialog.querySelector('[data-md-lightbox-track]');
  const counter = dialog.querySelector('[data-md-lightbox-counter]');
  const prev = dialog.querySelector('[data-md-lightbox-prev]');
  const next = dialog.querySelector('[data-md-lightbox-next]');

  const close = () => {
    if (dialog.open) dialog.close();
    document.body.classList.remove('md-pdp-lightbox-open');
  };

  dialog.addEventListener('close', () => {
    document.body.classList.remove('md-pdp-lightbox-open');
  });

  dialog.addEventListener('cancel', (e) => {
    e.preventDefault();
    close();
  });

  dialog.querySelectorAll('[data-md-lightbox-close]').forEach((btn) => {
    btn.addEventListener('click', close);
  });

  const go = (delta) => {
    if (!this._lightboxSlideCount) return;
    this._lightboxIndex =
      (this._lightboxIndex + delta + this._lightboxSlideCount) % this._lightboxSlideCount;
    this.updateLightboxSlide();
  };

  if (prev) prev.addEventListener('click', () => go(-1));
  if (next) next.addEventListener('click', () => go(1));

  dialog.addEventListener('keydown', (e) => {
    if (!dialog.open) return;
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      go(-1);
    }
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      go(1);
    }
  });

  const viewport = dialog.querySelector('.md-pdp-lightbox__viewport');
  if (viewport) {
    let touchStartX = 0;
    viewport.addEventListener(
      'touchstart',
      (e) => {
        touchStartX = e.changedTouches[0].clientX;
      },
      { passive: true }
    );
    viewport.addEventListener(
      'touchend',
      (e) => {
        const dx = e.changedTouches[0].clientX - touchStartX;
        if (Math.abs(dx) < 40) return;
        go(dx < 0 ? 1 : -1);
      },
      { passive: true }
    );
  }

  this._galleryLightbox = { dialog, track, counter, prev, next, close };
  return this._galleryLightbox;
};

BelabellyProductPage.prototype.buildLightboxSlides = function (imageIds) {
  const map = this.galleryMap;
  const lb = this.ensureGalleryLightbox();
  if (!lb || !map || !map.images || !lb.track) return false;

  const slides = [];
  imageIds.forEach((id) => {
    const data = map.images[String(id)];
    if (data && data.src) {
      slides.push({ id: String(id), src: data.src, alt: data.alt || '' });
    }
  });

  if (!slides.length) return false;

  lb.track.innerHTML = '';
  slides.forEach((slide, index) => {
    const li = document.createElement('li');
    li.className = 'md-pdp-lightbox__slide';
    li.setAttribute('role', 'listitem');
    li.setAttribute('data-md-lightbox-slide', '');
    li.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
    const img = document.createElement('img');
    img.src = slide.src;
    img.alt = slide.alt;
    img.loading = index === 0 ? 'eager' : 'lazy';
    li.appendChild(img);
    lb.track.appendChild(li);
  });

  this._lightboxSlideCount = slides.length;
  return true;
};

BelabellyProductPage.prototype.updateLightboxSlide = function () {
  const lb = this._galleryLightbox;
  if (!lb || !lb.track) return;

  const index = this._lightboxIndex || 0;
  const total = this._lightboxSlideCount || 0;
  lb.track.style.transform = `translate3d(-${index * 100}%, 0, 0)`;

  lb.track.querySelectorAll('[data-md-lightbox-slide]').forEach((slide, i) => {
    slide.setAttribute('aria-hidden', i === index ? 'false' : 'true');
  });

  if (lb.counter) {
    const tpl = this.root.dataset.galleryCounterTemplate || '__CURRENT__ de __TOTAL__';
    lb.counter.textContent = tpl
      .replace('__CURRENT__', String(index + 1))
      .replace('__TOTAL__', String(total));
  }

  if (lb.prev) lb.prev.disabled = total <= 1;
  if (lb.next) lb.next.disabled = total <= 1;
};

BelabellyProductPage.prototype.getLightboxImageIds = function (variant) {
  const v =
    variant ||
    this.getPickerVariant() ||
    (this.getSelectedVariantId() ? { id: this.getSelectedVariantId() } : null);
  const variantIds = this.getVariantImageIds(v);
  if (!variantIds.length) return [];
  return this.buildGalleryFullIds(variantIds);
};

BelabellyProductPage.prototype.openGalleryLightbox = function (startImageId) {
  const map = this.galleryMap;
  let imageIds = this.getLightboxImageIds();
  if (!imageIds.length) return;

  const startId = startImageId != null ? String(startImageId) : null;
  if (startId && imageIds.indexOf(startId) === -1 && map && map.images && map.images[startId]) {
    imageIds = [startId].concat(imageIds.filter(function (id) {
      return String(id) !== startId;
    }));
  }

  if (!this.buildLightboxSlides(imageIds)) return;

  let startIndex = 0;
  if (startId) {
    const idx = imageIds.indexOf(startId);
    if (idx >= 0) startIndex = idx;
  }

  this._lightboxIndex = startIndex;
  this.updateLightboxSlide();

  const lb = this._galleryLightbox;
  if (lb && lb.dialog && !lb.dialog.open) {
    lb.dialog.showModal();
    document.body.classList.add('md-pdp-lightbox-open');
  }
};

BelabellyProductPage.prototype.bindGalleryZoom = function () {
  const grid = this.root.querySelector('[data-md-gallery-grid]');
  if (!grid) return;
  grid.addEventListener('click', (e) => {
    const cell = e.target.closest('[data-md-gallery-cell]');
    if (!cell) return;
    e.preventDefault();
    const imageId = cell.getAttribute('data-image-id');
    this.openGalleryLightbox(imageId);
  });
};

BelabellyProductPage.prototype.getGalleryVideoSource = function (cell) {
  if (!cell) return null;
  const video = cell.querySelector('video');
  if (video) {
    const sourceEl = video.querySelector('source');
    if (sourceEl && sourceEl.src) return sourceEl.src;
    if (video.currentSrc) return video.currentSrc;
    if (video.src) return video.src;
  }
  const iframe = cell.querySelector('iframe');
  if (iframe && iframe.src) return iframe.src;
  const cdn = cell.getAttribute('data-video-cdn-url');
  if (cdn) return cdn;
  return null;
};

BelabellyProductPage.prototype.pauseGalleryAmbientVideos = function (paused) {
  this.root.querySelectorAll('[data-md-gallery-video-cell] video').forEach((video) => {
    if (paused) {
      video.dataset.mdWasPlaying = video.paused ? '0' : '1';
      video.pause();
      return;
    }
    if (video.dataset.mdWasPlaying === '1') {
      video.play().catch(function () {});
    }
    delete video.dataset.mdWasPlaying;
  });
};

BelabellyProductPage.prototype.ensureGalleryVideoLightbox = function () {
  if (this._galleryVideoLightbox) return this._galleryVideoLightbox;

  const dialog = this.root.querySelector('[data-md-gallery-video-lightbox]');
  if (!(dialog instanceof HTMLDialogElement)) return null;

  const player = dialog.querySelector('[data-md-video-lightbox-player]');
  if (!(player instanceof HTMLVideoElement)) return null;

  const close = () => {
    player.pause();
    player.removeAttribute('src');
    player.querySelectorAll('source').forEach((source) => source.removeAttribute('src'));
    player.load();
    if (dialog.open) dialog.close();
    document.body.classList.remove('md-pdp-lightbox-open');
    this.pauseGalleryAmbientVideos(false);
  };

  dialog.addEventListener('close', () => {
    document.body.classList.remove('md-pdp-lightbox-open');
    this.pauseGalleryAmbientVideos(false);
  });

  dialog.addEventListener('cancel', (e) => {
    e.preventDefault();
    close();
  });

  dialog.querySelectorAll('[data-md-video-lightbox-close]').forEach((btn) => {
    btn.addEventListener('click', close);
  });

  this._galleryVideoLightbox = { dialog, player, close };
  return this._galleryVideoLightbox;
};

BelabellyProductPage.prototype.openGalleryVideoLightbox = function (fromCell) {
  const cell = fromCell || this.root.querySelector('[data-md-gallery-video-cell]');
  const src = this.getGalleryVideoSource(cell);
  if (!src) return;

  const lb = this.ensureGalleryVideoLightbox();
  if (!lb || !lb.dialog || !lb.player) return;

  this.pauseGalleryAmbientVideos(true);

  const player = lb.player;
  let sourceEl = player.querySelector('source');
  if (!sourceEl) {
    sourceEl = document.createElement('source');
    sourceEl.type = 'video/mp4';
    player.appendChild(sourceEl);
  }
  sourceEl.src = src;
  player.muted = false;
  player.defaultMuted = false;
  player.controls = true;
  player.loop = true;
  player.playsInline = true;
  player.setAttribute('controls', '');
  player.removeAttribute('muted');
  player.load();

  if (!lb.dialog.open) {
    lb.dialog.showModal();
    document.body.classList.add('md-pdp-lightbox-open');
  }

  player.play().catch(function () {});
};

BelabellyProductPage.prototype.bindGalleryVideoExpand = function () {
  const grid = this.root.querySelector('[data-md-gallery-grid]');
  if (!grid) return;

  grid.addEventListener('dblclick', (e) => {
    const cell = e.target.closest('[data-md-gallery-video-cell]');
    if (!cell) return;
    e.preventDefault();
    e.stopPropagation();
    this.openGalleryVideoLightbox(cell);
  });
};

BelabellyProductPage.prototype.cacheGalleryVideoNode = function () {
  const grid = this.root.querySelector('[data-md-gallery-grid]');
  if (!grid || this._galleryVideoNode) return;
  const videoCell = grid.querySelector('[data-md-gallery-video-cell]');
  if (videoCell) {
    this._galleryVideoNode = videoCell.cloneNode(true);
  }
};

BelabellyProductPage.prototype.syncGalleryVideoAutoplay = function (scope) {
  const root = scope || this.root;
  if (!root) return;

  if (window.BelabellyGalleryVideoUnmute && typeof window.BelabellyGalleryVideoUnmute.sync === 'function') {
    window.BelabellyGalleryVideoUnmute.sync(root);
    return;
  }

  root.querySelectorAll('[data-md-gallery-video-cell] .deferred-media__poster-button').forEach((btn) => {
    btn.style.display = 'none';
  });
  root.querySelectorAll('[data-md-gallery-video-cell] video').forEach((video) => {
    video.muted = true;
    video.defaultMuted = true;
    video.autoplay = true;
    video.loop = true;
    video.playsInline = true;
    video.controls = false;
    video.removeAttribute('controls');
    video.removeAttribute('poster');
    video.setAttribute('muted', '');
    video.setAttribute('autoplay', '');
    video.setAttribute('loop', '');
    video.setAttribute('playsinline', '');
    video.classList.add('md-pdp__gallery-video-player--ambient');
    const playAttempt = video.play();
    if (playAttempt && typeof playAttempt.catch === 'function') {
      playAttempt.catch(function () {});
    }
  });
};

BelabellyProductPage.prototype.buildGalleryVideoCellFromMap = function () {
  const map = this.galleryMap;
  if (!map || !map.hasGalleryVideo) return null;

  const cell = document.createElement('div');
  cell.className = 'md-pdp__gallery-cell md-pdp__gallery-cell--video md-pdp__gallery-cell--fixed';
  cell.setAttribute('role', 'listitem');
  cell.setAttribute('data-md-gallery-video-cell', '');
  cell.setAttribute('data-md-gallery-fixed-video', '');
  cell.setAttribute('data-gallery-slot', '2');

  const frame = document.createElement('div');
  frame.className = 'md-pdp__gallery-video-frame';

  if (map.videoCdnUrl) {
    cell.setAttribute('data-video-cdn-url', map.videoCdnUrl);
    const video = document.createElement('video');
    video.className = 'md-pdp__gallery-video-player md-pdp__gallery-video-player--ambient';
    video.autoplay = true;
    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.disablePictureInPicture = true;
    video.setAttribute('autoplay', '');
    video.setAttribute('muted', '');
    video.setAttribute('loop', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('fetchpriority', 'high');
    if (map.videoPosterUrl) {
      video.setAttribute('poster', map.videoPosterUrl);
    }
    const source = document.createElement('source');
    source.src = map.videoCdnUrl;
    source.type = 'video/mp4';
    video.appendChild(source);
    frame.appendChild(video);
  }

  if (window.BelabellyGalleryVideoUnmute && typeof window.BelabellyGalleryVideoUnmute.createButton === 'function') {
    frame.appendChild(window.BelabellyGalleryVideoUnmute.createButton());
  }

  cell.appendChild(frame);
  return cell;
};

BelabellyProductPage.prototype.detachGalleryVideoCell = function () {
  const grid = this.root.querySelector('[data-md-gallery-grid]');
  if (!grid) return null;
  const cell = grid.querySelector('[data-md-gallery-video-cell]');
  if (!cell) return null;
  cell.remove();
  return cell;
};

BelabellyProductPage.prototype.appendGalleryVideoCell = function (grid, preservedCell) {
  if (preservedCell) {
    grid.appendChild(preservedCell);
    this.syncGalleryVideoAutoplay(grid);
    return;
  }

  this.cacheGalleryVideoNode();
  if (this._galleryVideoNode) {
    grid.appendChild(this._galleryVideoNode.cloneNode(true));
  } else {
    const built = this.buildGalleryVideoCellFromMap();
    if (built) {
      grid.appendChild(built);
    } else {
      const empty = document.createElement('div');
      empty.className = 'md-pdp__gallery-cell md-pdp__gallery-cell--empty';
      empty.setAttribute('role', 'listitem');
      empty.setAttribute('aria-hidden', 'true');
      grid.appendChild(empty);
      return;
    }
  }
  this.syncGalleryVideoAutoplay(grid);
};

BelabellyProductPage.prototype.clearGalleryColorSlot = function (slot) {
  const grid = this.root.querySelector('[data-md-gallery-grid]');
  if (!grid) return false;

  const cell = grid.querySelector('[data-md-gallery-cell][data-gallery-slot="' + slot + '"]');
  if (!cell) return false;

  const hadImage = cell.hasAttribute('data-image-id');
  cell.removeAttribute('data-image-id');
  const img = cell.querySelector('img');
  if (img) {
    img.removeAttribute('src');
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    img.alt = '';
  }
  cell.classList.add('md-pdp__gallery-cell--empty');
  return hadImage;
};

BelabellyProductPage.prototype.findGallerySlotCell = function (grid, slot) {
  if (!grid) return null;
  return (
    grid.querySelector('[data-md-gallery-cell][data-gallery-slot="' + slot + '"]') ||
    grid.querySelector('[data-gallery-slot="' + slot + '"]')
  );
};

BelabellyProductPage.prototype.ensureGalleryImageCell = function (grid, slot) {
  let cell = this.findGallerySlotCell(grid, slot);
  if (!cell) return null;

  if (cell.tagName === 'DIV' && cell.classList.contains('md-pdp__gallery-cell--empty')) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'md-pdp__gallery-cell';
    btn.setAttribute('role', 'listitem');
    btn.setAttribute('data-md-gallery-cell', '');
    btn.setAttribute('data-gallery-slot', slot);
    btn.setAttribute(
      'aria-label',
      (this.root.dataset.galleryZoomLabel || 'Ampliar foto __INDEX__').replace('__INDEX__', slot)
    );
    cell.replaceWith(btn);
    cell = btn;
  }

  let img = cell.querySelector('img');
  if (!img) {
    img = document.createElement('img');
    img.className = 'md-pdp__gallery-cell-img';
    img.loading = slot === '1' ? 'eager' : 'lazy';
    img.sizes = '(min-width: 990px) 26vw, 46vw';
    cell.appendChild(img);
  }

  cell.classList.remove('md-pdp__gallery-cell--empty');
  cell.removeAttribute('aria-hidden');
  return cell;
};

BelabellyProductPage.prototype.updateGalleryColorSlot = function (slot, imageId) {
  const grid = this.root.querySelector('[data-md-gallery-grid]');
  const map = this.galleryMap;
  if (!grid || !map || !map.images || !imageId || imageId === 'video') return false;

  const data = map.images[String(imageId)];
  if (!data || !data.src900) return false;

  const cell = this.ensureGalleryImageCell(grid, slot);
  if (!cell) return false;

  const img = cell.querySelector('img');
  if (!img) return false;

  cell.classList.remove('md-pdp__gallery-cell--empty');

  const nextId = String(imageId);
  const nextSrc = data.src900;
  const changed = cell.getAttribute('data-image-id') !== nextId || img.getAttribute('src') !== nextSrc;

  cell.setAttribute('data-gallery-slot', slot);
  cell.setAttribute('data-image-id', nextId);
  img.alt = data.alt || '';
  if (changed) {
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    img.src = nextSrc;
  }
  if (slot === '1') {
    this.syncStoryProductImage(data.src || nextSrc);
  }
  return true;
};

BelabellyProductPage.prototype.updateGalleryPrimaryImage = function (imageId) {
  return this.updateGalleryColorSlot('1', imageId);
};

BelabellyProductPage.prototype.syncStoryProductImage = function (src) {
  const img = document.querySelector('.belabelly-pdp-story__image--product');
  if (!img || !src) return;
  img.removeAttribute('srcset');
  img.src = src;
};

BelabellyProductPage.prototype.updateGalleryColorImages = function (gridIds) {
  if (!gridIds || !gridIds.length) return false;
  const back = this.getGalleryBackSlot();
  const frontOk =
    gridIds[0] && gridIds[0] !== 'video' ? this.updateGalleryColorSlot('1', gridIds[0]) : true;
  const backId = gridIds[back.index];
  const needsBack = !!(backId && backId !== 'video');
  const backOk = needsBack ? this.updateGalleryColorSlot(back.attr, backId) : true;
  return frontOk && backOk;
};

BelabellyProductPage.prototype.galleryHasFixedLayout = function () {
  const grid = this.root.querySelector('[data-md-gallery-grid]');
  const map = this.galleryMap;
  if (!grid) return false;
  if (map && map.hasGalleryVideo) return true;
  return !!(
    grid.querySelector('[data-md-gallery-fixed-video]') ||
    grid.querySelector('[data-md-gallery-cell][data-gallery-slot="2"]') ||
    grid.querySelector('[data-md-gallery-cell][data-gallery-slot="3"]') ||
    grid.querySelector('[data-md-gallery-cell][data-gallery-slot="4"]')
  );
};

BelabellyProductPage.prototype.renderGalleryWithImageIds = function (gridIds, fullImageIds) {
  const grid = this.root.querySelector('[data-md-gallery-grid]');
  const map = this.galleryMap;
  const slotIds = gridIds || fullImageIds;
  if (!grid || !map || !map.images || !slotIds || !slotIds.length) return;

  const maxSlots = this.getMaxGallerySlots();
  const totalImages = (fullImageIds && fullImageIds.length) || slotIds.filter((id) => id && id !== 'video').length;
  const moreCount = Math.max(0, totalImages - maxSlots);
  const zoomLabel = this.root.dataset.galleryZoomLabel || 'Ampliar foto __INDEX__';
  const moreBadgeTpl =
    this.root.dataset.galleryMoreBadge || '+__COUNT__';

  const preservedVideoCell = this.detachGalleryVideoCell();
  grid.dataset.totalImages = String(totalImages);
  grid.innerHTML = '';

  let fallbackId = null;
  let fallbackData = null;
  for (let i = 0; i < slotIds.length; i += 1) {
    const candidateId = slotIds[i];
    if (candidateId == null || candidateId === 'video') continue;
    const candidateData = map.images[String(candidateId)];
    if (candidateData && candidateData.src900) {
      fallbackId = candidateId;
      fallbackData = candidateData;
      break;
    }
  }

  for (let slot = 0; slot < maxSlots; slot += 1) {
    let id = slotIds[slot];
    const slotNumber = String(slot + 1);

    if (id === 'video') {
      this.appendGalleryVideoCell(grid, preservedVideoCell);
      continue;
    }

    let data = id != null ? map.images[String(id)] : null;
    if ((!data || !data.src900) && fallbackData) {
      id = fallbackId;
      data = fallbackData;
    }

    if (data && data.src900) {
      const btn = document.createElement('button');
      btn.type = 'button';
      const isLastWithMore = slot === maxSlots - 1 && moreCount > 0;
      btn.className = 'md-pdp__gallery-cell' + (isLastWithMore ? ' md-pdp__gallery-cell--has-more' : '');
      btn.setAttribute('role', 'listitem');
      btn.setAttribute('data-md-gallery-cell', '');
      btn.setAttribute('data-gallery-slot', slotNumber);
      btn.setAttribute('data-image-id', String(id));
      btn.setAttribute('aria-label', zoomLabel.replace('__INDEX__', slotNumber));
      const img = document.createElement('img');
      img.className = 'md-pdp__gallery-cell-img';
      img.alt = data.alt || '';
      img.loading = slot === 0 ? 'eager' : 'lazy';
      img.removeAttribute('src');
      img.src = data.src900;
      img.sizes = '(min-width: 990px) 28vw, 48vw';
      btn.appendChild(img);
      if (isLastWithMore) {
        const badge = document.createElement('span');
        badge.className = 'md-pdp__gallery-cell-more';
        badge.setAttribute('aria-hidden', 'true');
        badge.textContent = moreBadgeTpl.replace('__COUNT__', String(moreCount));
        btn.appendChild(badge);
      }
      grid.appendChild(btn);
    }
  }

  if (preservedVideoCell && !grid.querySelector('[data-md-gallery-video-cell]')) {
    this._galleryVideoNode = preservedVideoCell;
  }

  this.syncGalleryVideoAutoplay(grid);
};

BelabellyProductPage.prototype.renderGalleryFromFeaturedMedia = function (variant) {
  const variantIds = this.getVariantImageIds(variant);
  if (variantIds.length) {
    const gridIds = this.getGridImageIdsFromList(variantIds);
    this.renderGalleryWithImageIds(gridIds, this.buildGalleryFullIds(variantIds));
    return true;
  }

  return false;
};

BelabellyProductPage.prototype.updateGalleryFromVariant = function (variant) {
  if (!variant || !variant.id) return;

  if (this._galleryLightbox && this._galleryLightbox.dialog && this._galleryLightbox.dialog.open) {
    this._galleryLightbox.close();
  }

  const variantIds = this.getVariantImageIds(variant);
  if (!variantIds.length) return;

  const gridIds = this.getGridImageIdsFromList(variantIds);
  const backSlot = this.getGalleryBackSlot();
  const primaryId = gridIds[0];
  const backId = gridIds[backSlot.index];
  const grid = this.root.querySelector('[data-md-gallery-grid]');
  const slot1 = grid && grid.querySelector('[data-md-gallery-cell][data-gallery-slot="1"]');
  const slotBack =
    grid && grid.querySelector('[data-md-gallery-cell][data-gallery-slot="' + backSlot.attr + '"]');
  const currentPrimaryId = slot1 && slot1.getAttribute('data-image-id');
  const currentBackId = slotBack && slotBack.getAttribute('data-image-id');
  const groupValue = this.resolveVisualGroupValue() || '';

  if (
    primaryId &&
    primaryId !== 'video' &&
    String(currentPrimaryId) === String(primaryId) &&
    (!backId || backId === 'video' || String(currentBackId) === String(backId))
  ) {
    return;
  }

  const hasGalleryVideo = Boolean(this.galleryMap && this.galleryMap.hasGalleryVideo);

  if (
    !hasGalleryVideo &&
    this.galleryHasFixedLayout() &&
    primaryId &&
    primaryId !== 'video'
  ) {
    if (this.updateGalleryColorImages(gridIds)) {
      if (window.BelabellyPdpGallerySync) {
        window.BelabellyPdpGallerySync.noteVisualChange(groupValue, variantIds);
      }
      this.syncGalleryVideoAutoplay(grid);
      return;
    }
  }

  this.renderGalleryWithImageIds(gridIds, this.buildGalleryFullIds(variantIds));
  if (window.BelabellyPdpGallerySync) {
    window.BelabellyPdpGallerySync.noteVisualChange(groupValue, variantIds);
  }
};

BelabellyProductPage.prototype.getPickerVariant = function () {
  const script = this.root.querySelector(
    'variant-picker.belabelly-pdp-variant-picker script[type="application/json"]'
  );
  if (!script || !script.textContent) return null;
  try {
    return JSON.parse(script.textContent);
  } catch {
    return null;
  }
};

BelabellyProductPage.prototype.normOptionValue = function (value) {
  if (value == null) return '';
  return String(value).trim();
};

BelabellyProductPage.prototype.collectPickerSelections = function () {
  const picker = this.root.querySelector('variant-picker.belabelly-pdp-variant-picker');
  if (!picker) return {};

  const selections = {};
  const norm = (v) => this.normOptionValue(v);

  picker.querySelectorAll('[data-md-option-position]').forEach((el) => {
    const pos = el.getAttribute('data-md-option-position');
    const val = el.getAttribute('data-md-option-value');
    if (pos && val) selections[pos] = norm(val);
  });

  picker.querySelectorAll('fieldset input[type="radio"]:checked').forEach((input) => {
    const pos = input.dataset.inputId ? input.dataset.inputId.split('-')[0] : '';
    if (pos) selections[pos] = norm(input.value);
  });

  picker.querySelectorAll('select.variant-option__select, select.md-pdp-variant-select').forEach((select) => {
    const option = select.options[select.selectedIndex];
    if (!option || !option.value || option.disabled) return;
    const pos = option.dataset.inputId ? option.dataset.inputId.split('-')[0] : '';
    if (pos) selections[pos] = norm(option.value);
  });

  return selections;
};

BelabellyProductPage.prototype.getAllVariants = function () {
  const map = this.galleryMap;
  if (map && Array.isArray(map.allVariants) && map.allVariants.length) {
    return map.allVariants;
  }

  const el = this.root.querySelector('[data-md-all-variants]');
  if (!el || !el.textContent) return [];

  try {
    return JSON.parse(el.textContent);
  } catch {
    return [];
  }
};

BelabellyProductPage.prototype.matchVariantFromSelections = function () {
  const allVariants = this.getAllVariants();
  const selections = this.collectPickerSelections();
  const keys = Object.keys(selections);
  if (!allVariants.length || !keys.length) return null;

  const norm = (v) => this.normOptionValue(v);

  for (let i = 0; i < allVariants.length; i += 1) {
    const variant = allVariants[i];
    let ok = true;

    for (let k = 0; k < keys.length; k += 1) {
      const pos = Number(keys[k]);
      const selected = selections[keys[k]];
      if (pos === 1 && norm(variant.option1) !== selected) ok = false;
      else if (pos === 2 && norm(variant.option2) !== selected) ok = false;
      else if (pos === 3 && norm(variant.option3) !== selected) ok = false;
    }

    if (ok) return variant;
  }

  return null;
};

BelabellyProductPage.prototype.syncFollowerVariant = function () {
  if (this.followerMulti) {
    this.followerMulti.refresh({ forceGrid: true });
    return true;
  }

  this.syncFollowerPriceFromStamp();
  return true;
};

BelabellyProductPage.prototype.bindFollowerVariantSync = function () {
  if (this.root.dataset.pdpFollowerRetail !== 'true') return;

  const self = this;
  const picker = this.root.querySelector('variant-picker.belabelly-pdp-variant-picker');
  if (!picker) return;

  const scheduleSync = () => {
    self.syncFollowerVariant();
    window.setTimeout(() => self.syncFollowerVariant(), 80);
    window.setTimeout(() => self.syncFollowerVariant(), 220);
  };

  picker.addEventListener('change', scheduleSync);
  picker.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (
      target.closest('.belabelly-pdp-variant-option--estampa') ||
      (target instanceof HTMLInputElement && target.type === 'radio')
    ) {
      scheduleSync();
    }
  });

  document.addEventListener(ThemeEvents.variantUpdate, (event) => {
    const productId = event.detail && event.detail.data && event.detail.data.productId;
    if (productId && String(productId) !== String(self.root.dataset.productId)) return;
    scheduleSync();
  });

  window.requestAnimationFrame(scheduleSync);
};

BelabellyProductPage.prototype.resolveActiveVariant = function () {
  const fromSelections = this.matchVariantFromSelections();
  if (fromSelections && fromSelections.id) return fromSelections;

  const resolve =
    window.BelabellyPdpVariant && typeof window.BelabellyPdpVariant.resolve === 'function'
      ? window.BelabellyPdpVariant.resolve
      : null;
  if (resolve) {
    const fromExternal = resolve(this.root);
    if (fromExternal?.id) return fromExternal;
  }

  return this.getPickerVariant();
};

BelabellyProductPage.prototype.bindFormVariantSync = function () {
  const form = this.root.querySelector('[data-md-product-form]');
  if (!form) return;

  form.addEventListener('submit', () => {
    const input = form.querySelector('[name="id"]');
    if (!input) return;
    const variant = this.resolveActiveVariant();
    if (variant?.id) input.value = String(variant.id);
  });
};

BelabellyProductPage.prototype.bindVariantPickerGallery = function () {
  /* Galeria na troca de variante: belabelly-pdp-sticky-sync.js + variant:update */
};

BelabellyProductPage.prototype.setCommerceBusy = function (busy) {
  const buyNow = this.root.querySelector('[data-md-buy-now]');
  const stickyBtn = document.querySelector('[data-md-sticky-buy-now]');

  [buyNow, stickyBtn].forEach((el) => {
    if (!el) return;
    el.classList.toggle('md-pdp__btn--busy', busy);
    if (busy) el.setAttribute('aria-busy', 'true');
    else el.removeAttribute('aria-busy');
  });
};

BelabellyProductPage.prototype.resetCommerceState = function () {
  this.setCommerceBusy(false);

  const buyNow = this.root.querySelector('[data-md-buy-now]');
  const stickyBtn = document.querySelector('[data-md-sticky-buy-now]');

  [buyNow, stickyBtn].forEach((el) => {
    if (!el) return;
    delete el.dataset.mdSubmitting;
    el.removeAttribute('disabled');
  });

  const variant = this.getPickerVariant();
  if (variant) {
    this.updateAvailability(variant);
  } else if (this.followerMulti) {
    this.followerMulti.updateCommerce();
    return;
  }

  const input = this.root.querySelector('[data-md-product-form] [name="id"]');
  if (input && input.value && buyNow) buyNow.removeAttribute('disabled');
};

BelabellyProductPage.prototype.bindAddCartBusy = function () {
  const self = this;
  const form = this.root.querySelector('[data-md-product-form]');
  if (!form) return;

  const onDone = () => {
    self.setCommerceBusy(false);
    form.querySelectorAll('[data-md-buy-now]').forEach((el) => {
      delete el.dataset.mdSubmitting;
    });
  };

  document.addEventListener('cart:update', onDone);
  document.addEventListener('cart:error', onDone);
};

BelabellyProductPage.prototype.bindKitValidationClear = function () {
  const self = this;
  const onChange = function (event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (
      target.closest('[data-md-kit-sizes], [data-md-kit-extra-size], [data-md-gravacao-fields]')
    ) {
      clearKitValidation(self.root);
    }
  };
  this.root.addEventListener('input', onChange);
  this.root.addEventListener('change', onChange);
};

BelabellyProductPage.prototype.bindBuyNow = function () {
  const self = this;
  const btn = this.root.querySelector('[data-md-buy-now]');
  const form = this.root.querySelector('[data-md-product-form]');
  if (!btn || !form) return;

  btn.addEventListener('click', async (e) => {
    e.preventDefault();
    if (btn.disabled || btn.dataset.mdSubmitting === 'true' || btn.classList.contains('md-pdp__btn--busy')) {
      return;
    }

    if (self.followerMulti) {
      if (!validateFormAssociatedFields(form, self.root)) return;
      const items = self.followerMulti.buildCartItems();
      if (!items.length) return;

      btn.dataset.mdSubmitting = 'true';
      self.setCommerceBusy(true);

      try {
        const properties = collectLineItemProperties(form);
        if (Object.keys(properties).length) {
          items.forEach((item) => {
            item.properties = { ...(item.properties || {}), ...properties };
          });
        }

        const formComponent = self.root.querySelector('product-form-component');

        await addFollowerItemsToCart(items, {
          source: 'belabelly-pdp-follower-multi',
          sourceId: formComponent?.getAttribute('data-section-id') || '',
          productId: self.root.dataset.productId || '',
          itemCount: self.followerMulti.getTotalQuantity(),
          errorMessage:
            self.root.dataset.cartAddError ||
            'Não foi possível adicionar ao carrinho. Confira estampa e tamanhos.',
        });

        self.followerMulti.quantities.clear();
        self.followerMulti.renderSizeGrid();
        self.followerMulti.updateCommerce();
        self.resetCommerceState();
      } catch (err) {
        self.resetCommerceState();
        const message = err instanceof Error ? err.message : String(err);
        window.alert(message);
      }
      return;
    }

    const variantInput = form.querySelector('[name="id"]');
    if (!variantInput) return;

    const resolved = self.resolveActiveVariant();
    if (resolved?.id) variantInput.value = String(resolved.id);
    if (!variantInput.value) return;
    if (!validateFormAssociatedFields(form, self.root)) return;

    btn.dataset.mdSubmitting = 'true';
    self.setCommerceBusy(true);

    try {
      const properties = collectLineItemProperties(form);
      const item = { id: Number(variantInput.value), quantity: 1 };
      if (Object.keys(properties).length) item.properties = properties;

      await addItemsAndGoCart([item], {
        errorMessage: self.root.dataset.checkoutError || 'Não foi possível adicionar ao carrinho. Confira tamanhos e gravações.',
      });
    } catch (err) {
      self.resetCommerceState();
      const message = err instanceof Error ? err.message : String(err);
      window.alert(message);
    }
  });
};

BelabellyProductPage.prototype.bindNavScroll = function () {
  const links = this.root.querySelectorAll('[data-md-anchor]');
  links.forEach((link) => {
    link.addEventListener('click', (e) => {
      const href = link.getAttribute('href');
      if (!href || href.indexOf('#') !== 0) return;
      const target = document.querySelector(href);
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      links.forEach((l) => l.classList.remove('is-active'));
      link.classList.add('is-active');
    });
  });
};

BelabellyProductPage.prototype.bindVariantUpdates = function () {
  const self = this;
  const productId = String(this.root.dataset.productId || '');

  const handler = (event) => {
    const eventProductId = event.detail && event.detail.data && event.detail.data.productId;

    if (eventProductId && String(eventProductId) !== productId) return;

    if (self.root.dataset.pdpFollowerRetail === 'true') {
      self.syncFollowerVariant();
      return;
    }

    const resolved = self.resolveActiveVariant();
    const variant =
      resolved && resolved.id
        ? resolved
        : event.detail && event.detail.resource;

    if (variant && variant.price != null) {
      self.updatePrices(variant);
    }
    if (variant && variant.id) {
      self.updateAvailability(variant);
      const input = self.root.querySelector('[data-md-product-form] [name="id"]');
      if (input) input.value = String(variant.id);
    }
  };

  const section = this.root.closest('.shopify-section');
  if (section) section.addEventListener(ThemeEvents.variantUpdate, handler);
  document.addEventListener(ThemeEvents.variantUpdate, handler);
};

BelabellyProductPage.prototype.updatePrices = function (variant) {
  const price = variant.price;
  const compare =
    variant.compare_at_price && variant.compare_at_price > price ? variant.compare_at_price : null;
  const pix = Math.round(price * (1 - this.pixDiscount));
  const installment = Math.round(price / this.installments);
  const savings = compare ? compare - price : 0;
  const onSale = Boolean(compare && compare > price);

  const compareEl = this.root.querySelector('[data-md-compare]');
  const priceHeroEl = this.root.querySelector('[data-md-price-hero]');
  const pixEl = this.root.querySelector('[data-md-pix]');
  const instEl = this.root.querySelector('[data-md-installments]');
  const saveEl = this.root.querySelector('[data-md-savings]');
  const badgeEl = this.root.querySelector('[data-md-discount-badge]');
  const dropWrap = this.root.querySelector('.md-pdp__offer-tags--promo');
  const dropEl = this.root.querySelector('[data-md-price-drop]');
  const shippingTag = this.root.querySelector('[data-md-shipping-tag]');
  const promoOff = this.root.querySelector('[data-md-promo-off]');
  const promoBar = this.root.querySelector('[data-md-promo-bar]');

  if (priceHeroEl) {
    priceHeroEl.textContent = formatMoney(price, this.moneyFormat, this.currencyCode);
  }
  if (pixEl) {
    const tpl = pixEl.dataset.template || '';
    pixEl.textContent = tpl.replace('__PRICE__', formatMoney(pix, this.moneyFormat, this.currencyCode));
  }
  if (instEl) {
    const tpl = instEl.dataset.template || '';
    instEl.textContent = tpl
      .replace('__COUNT__', String(this.installments))
      .replace('__PRICE__', formatMoney(installment, this.moneyFormat, this.currencyCode));
  }

  if (onSale) {
    if (compareEl) {
      compareEl.classList.remove('hidden');
      compareEl.textContent = formatMoney(compare, this.moneyFormat, this.currencyCode);
    }
    if (saveEl) {
      saveEl.classList.remove('hidden');
      const saveValue = saveEl.querySelector('[data-md-savings-value]');
      if (saveValue) saveValue.textContent = formatMoney(savings, this.moneyFormat, this.currencyCode);
    }
    if (dropEl) dropEl.classList.remove('hidden');
    if (dropWrap) dropWrap.classList.remove('hidden');
    const pct = Math.round((savings * 100) / compare);
    if (badgeEl) {
      const tpl = badgeEl.dataset.template || '__PCT__% OFF';
      badgeEl.textContent = tpl.replace('__PCT__', String(pct)).replace('{{ percent }}', String(pct));
      badgeEl.classList.remove('hidden');
    }
    if (promoOff) promoOff.textContent = pct + '% OFF';
    if (promoBar) promoBar.classList.remove('hidden');
  } else {
    if (compareEl) compareEl.classList.add('hidden');
    if (saveEl) saveEl.classList.add('hidden');
    if (dropEl) dropEl.classList.add('hidden');
    if (dropWrap) {
      if (shippingTag && !shippingTag.classList.contains('hidden')) {
        dropWrap.classList.remove('hidden');
      } else {
        dropWrap.classList.add('hidden');
      }
    }
    if (badgeEl) badgeEl.classList.add('hidden');
    if (promoBar) promoBar.classList.add('hidden');
  }

  this.updateShippingTag(price);

  const cashbackEl = this.root.querySelector('[data-md-cashback]');
  if (cashbackEl && this.cashbackPercent > 0) {
    const cb = Math.round(price * this.cashbackPercent);
    const tpl = cashbackEl.dataset.template || '';
    cashbackEl.textContent = tpl.replace('__AMOUNT__', formatMoney(cb, this.moneyFormat, this.currencyCode));
  }
};

BelabellyProductPage.prototype.updateShippingTag = function (priceCents) {
  const tag = this.root.querySelector('[data-md-shipping-tag]');
  const text = tag ? tag.querySelector('[data-md-shipping-text]') : null;
  if (!tag || !text) return;

  const pix = Math.round(priceCents * (1 - this.pixDiscount));
  const qualifies = pix >= this.freeShippingMin;

  text.textContent = qualifies
    ? this.tplFreeShipping
    : this.tplFreeShippingFrom.replace('__AMOUNT__', formatMoney(this.freeShippingMin, this.moneyFormat, this.currencyCode));
};

BelabellyProductPage.prototype.resolveMaterialFromVariant = function (variant) {
  if (!variant) return '';
  const raw = this.root.dataset.productOptions || '';
  if (!raw) return '';
  const names = raw.split('||');
  const values = [variant.option1, variant.option2, variant.option3];
  for (let i = 0; i < names.length; i += 1) {
    const name = (names[i] || '').toLowerCase();
    const value = values[i];
    if (!value) continue;
    if (name.includes('material') || name.includes('metal') || name === 'ouro' || name === 'prata') {
      return value;
    }
  }
  return '';
};

BelabellyProductPage.prototype.updateMaterial = function (variant) {
  const nameEl = this.root.querySelector('[data-md-material-name]');
  if (!nameEl) return;
  const fromVariant = this.resolveMaterialFromVariant(variant);
  if (!fromVariant) return;
  nameEl.textContent = fromVariant;
  const a11y = this.root.querySelector('[data-md-material-a11y]');
  if (a11y) {
    const tpl = a11y.dataset.template || '';
    a11y.textContent = tpl.indexOf('__MATERIAL__') >= 0 ? tpl.replace('__MATERIAL__', fromVariant) : fromVariant;
  }
};

BelabellyProductPage.prototype.updateAvailability = function (variant) {
  const meta = this.root.querySelector('[data-md-stock]');
  const buyNow = this.root.querySelector('[data-md-buy-now]');
  const notify = this.root.querySelector('[data-md-notify]');
  const buyNowLabel = this.root.dataset.labelBuyNow || 'Adicionar ao carrinho';
  const policy = window.BelabellyInventoryPolicy;

  if (meta && policy) {
    policy.applyStockMeta(meta, variant);
  }
  if (buyNow) {
    buyNow.disabled = false;
    buyNow.textContent = buyNowLabel;
  }
  if (notify) notify.classList.add('hidden');
  this.updateMaterial(variant);
};

BelabellyProductPage.prototype.parsePromoEnd = function (raw) {
  const target = (raw || '').trim();
  if (!target) {
    const endOfDay = new Date();
    endOfDay.setHours(23, 59, 59, 999);
    return endOfDay.getTime();
  }
  const normalized = target.indexOf('T') >= 0 ? target : target + 'T23:59:59';
  let end = Date.parse(normalized.replace(' ', 'T'));
  if (Number.isNaN(end)) end = Date.parse(target);
  if (Number.isNaN(end)) {
    const endOfDay = new Date();
    endOfDay.setHours(23, 59, 59, 999);
    return endOfDay.getTime();
  }
  return end;
};

BelabellyProductPage.prototype.initCountdown = function () {
  const wrap = this.root.querySelector('[data-md-countdown]');
  if (!wrap) return;

  const els = {
    d: wrap.querySelector('[data-cd-days]'),
    h: wrap.querySelector('[data-cd-hours]'),
    m: wrap.querySelector('[data-cd-minutes]'),
    s: wrap.querySelector('[data-cd-seconds]'),
  };

  const end = this.parsePromoEnd(wrap.dataset.target || this.promoEnd);
  const pad = (n) => String(n).padStart(2, '0');

  const tick = () => {
    const diff = Math.max(0, end - Date.now());
    if (diff <= 0) {
      if (els.d) els.d.textContent = '00';
      if (els.h) els.h.textContent = '00';
      if (els.m) els.m.textContent = '00';
      if (els.s) els.s.textContent = '00';
      return;
    }
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff / 3600000) % 24);
    const m = Math.floor((diff / 60000) % 60);
    const s = Math.floor((diff / 1000) % 60);
    if (els.d) els.d.textContent = pad(d);
    if (els.h) els.h.textContent = pad(h);
    if (els.m) els.m.textContent = pad(m);
    if (els.s) els.s.textContent = pad(s);

    const daysWrap = wrap.querySelector('[data-cd-days-wrap]');
    const daysSep = wrap.querySelector('.countdown-bar__sep--days');
    const showDays = d > 0;
    if (daysWrap) daysWrap.classList.toggle('hidden', !showDays);
    if (daysSep) daysSep.classList.toggle('hidden', !showDays);
  };

  tick();
  window.setInterval(tick, 1000);
};

BelabellyProductPage.prototype.initDeliveryEstimate = function () {
  if (
    window.BelabellyPdpDeliveryEstimate &&
    typeof window.BelabellyPdpDeliveryEstimate.initRoot === 'function'
  ) {
    window.BelabellyPdpDeliveryEstimate.initRoot(this.root);
  }
};

BelabellyProductPage.prototype.initViewers = function () {
  return;
};

function initBelabellyProductPage() {
  const hash = (window.location.hash || '').replace('#', '');
  const hasContentAnchor =
    hash === 'md-pdp-ficha' || hash === 'md-pdp-descricao' || hash === 'md-pdp-detalhes';
  if (hasContentAnchor) {
    const target = document.getElementById(hash);
    if (target instanceof HTMLDetailsElement) {
      target.open = true;
    }
    requestAnimationFrame(() => {
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  document.querySelectorAll('[data-md-pdp]').forEach((root) => {
    if (root instanceof HTMLElement) {
      new BelabellyProductPage(root);
    }
  });

  if (window.BelabellyPdpCommerce && typeof window.BelabellyPdpCommerce.hardReset === 'function') {
    window.BelabellyPdpCommerce.hardReset();
  }
}

window.BelabellyPdpCommerce = window.BelabellyPdpCommerce || {};
window.BelabellyPdpCommerce.resetInstances = function () {
  document.querySelectorAll('[data-md-pdp]').forEach((root) => {
    if (root._belabellyPdp && typeof root._belabellyPdp.resetCommerceState === 'function') {
      root._belabellyPdp.resetCommerceState();
    }
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBelabellyProductPage);
} else {
  initBelabellyProductPage();
}
